import type {
  CalendarDate,
  CalendarDateRange,
  DateControlLocale,
  YearMonth,
} from './types';
import {
  addMonths,
  calendarDateToYearMonth,
  compareDate,
  compareMonth,
  formatMonth,
  getCalendarCells,
  getEarlierDate,
  getToday,
  isSameDate,
  isWithinRange,
  toDate,
  weekdayLabels,
} from './dateControlUtils';

interface DateCalendarPanelProps {
  visibleMonth: YearMonth;
  onVisibleMonthChange: (value: YearMonth) => void;
  locale: DateControlLocale;
  ariaLabel: string;
  selectedDate?: CalendarDate | null;
  range?: CalendarDateRange;
  showActions?: boolean;
  disableFuture?: boolean;
  minDate?: CalendarDate;
  maxDate?: CalendarDate;
  confirmDisabled?: boolean;
  onCancel?: () => void;
  onConfirm?: () => void;
  onDateSelect: (value: CalendarDate) => void;
}

const getEffectiveMaxDate = (
  maxDate: CalendarDate | undefined,
  disableFuture: boolean,
) => {
  if (!disableFuture) {
    return maxDate;
  }

  const today = getToday();

  return maxDate ? getEarlierDate(maxDate, today) : today;
};

export const DateCalendarPanel = ({
  visibleMonth,
  onVisibleMonthChange,
  locale,
  ariaLabel,
  selectedDate,
  range,
  showActions,
  disableFuture = true,
  minDate,
  maxDate,
  confirmDisabled,
  onCancel,
  onConfirm,
  onDateSelect,
}: DateCalendarPanelProps) => {
  const cells = getCalendarCells(visibleMonth);
  const prevMonth = addMonths(visibleMonth, -1);
  const prevYear = addMonths(visibleMonth, -12);
  const nextMonth = addMonths(visibleMonth, 1);
  const nextYear = addMonths(visibleMonth, 12);
  const effectiveMaxDate = getEffectiveMaxDate(maxDate, disableFuture);

  const canShowMonth = (month: YearMonth) => {
    if (minDate && compareMonth(month, calendarDateToYearMonth(minDate)) < 0) {
      return false;
    }

    if (
      effectiveMaxDate &&
      compareMonth(month, calendarDateToYearMonth(effectiveMaxDate)) > 0
    ) {
      return false;
    }

    return true;
  };

  const isDateDisabled = (date: CalendarDate) =>
    Boolean(
      (minDate && compareDate(date, minDate) < 0) ||
        (effectiveMaxDate && compareDate(date, effectiveMaxDate) > 0),
    );

  const moveMonth = (amount: number) => {
    const nextVisibleMonth = addMonths(visibleMonth, amount);

    if (!canShowMonth(nextVisibleMonth)) {
      return;
    }

    onVisibleMonthChange(nextVisibleMonth);
  };

  const selectDate = (date: CalendarDate) => {
    if (isDateDisabled(date)) {
      return;
    }

    onDateSelect(date);

    if (date.year !== visibleMonth.year || date.month !== visibleMonth.month) {
      onVisibleMonthChange({
        year: date.year,
        month: date.month,
      });
    }
  };

  return (
    <div className="date-calendar" aria-label={ariaLabel}>
      <div className="date-calendar__head">
        <button
          type="button"
          aria-label={locale === 'ko' ? '이전 년' : 'Previous year'}
          disabled={!canShowMonth(prevYear)}
          onClick={() => moveMonth(-12)}
        >
          «
        </button>
        <button
          type="button"
          aria-label={locale === 'ko' ? '이전 월' : 'Previous month'}
          disabled={!canShowMonth(prevMonth)}
          onClick={() => moveMonth(-1)}
        >
          ‹
        </button>
        <strong>{formatMonth(visibleMonth, locale)}</strong>
        <button
          type="button"
          aria-label={locale === 'ko' ? '다음 월' : 'Next month'}
          disabled={!canShowMonth(nextMonth)}
          onClick={() => moveMonth(1)}
        >
          ›
        </button>
        <button
          type="button"
          aria-label={locale === 'ko' ? '다음 년' : 'Next year'}
          disabled={!canShowMonth(nextYear)}
          onClick={() => moveMonth(12)}
        >
          »
        </button>
      </div>
      <div className="date-calendar__grid">
        {weekdayLabels[locale].map((day) => (
          <span className="date-calendar__weekday" key={day}>
            {day}
          </span>
        ))}
        {cells.map((cell) => {
          const isSelected = isSameDate(selectedDate ?? null, cell.date);
          const isRangeStart = isSameDate(range?.start ?? null, cell.date);
          const isRangeEnd = isSameDate(range?.end ?? null, cell.date);
          const isRange = isWithinRange(cell.date, range);
          const isSunday = toDate(cell.date).getDay() === 0;
          const isDisabled = isDateDisabled(cell.date);

          return (
            <button
              type="button"
              className={[
                !cell.isCurrentMonth ? 'is-muted' : '',
                isSunday ? 'is-sunday' : '',
                isSelected ? 'is-selected' : '',
                isRange ? 'is-range' : '',
                isRangeStart ? 'is-range-start' : '',
                isRangeEnd ? 'is-range-end' : '',
                isDisabled ? 'is-disabled' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              disabled={isDisabled}
              key={`${cell.date.year}-${cell.date.month}-${cell.date.day}`}
              onClick={() => selectDate(cell.date)}
            >
              {cell.date.day}
            </button>
          );
        })}
      </div>
      {showActions && (
        <div className="date-calendar__actions">
          <button type="button" className="lab-button lab-button--sm" onClick={onCancel}>
            {locale === 'ko' ? '취소' : 'Cancel'}
          </button>
          <button
            type="button"
            className="lab-button lab-button--sm lab-button--blue"
            disabled={confirmDisabled}
            onClick={onConfirm}
          >
            {locale === 'ko' ? '확인' : 'Confirm'}
          </button>
        </div>
      )}
    </div>
  );
};
